import type { PersonTask } from '../../lib/peopleApi';
import type { TaskPriority } from '../../lib/tasksApi';

// Status labels/colors now come from the task rows themselves (configurable
// statuses) — see task.statusLabel / task.statusColor.

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  URGENT: 'Urgent',
  HIGH: 'Ridicată',
  MEDIUM: 'Medie',
  LOW: 'Scăzută',
  NONE: 'Fără prioritate',
};

export function priorityLabel(priority: TaskPriority): string {
  return PRIORITY_LABEL[priority];
}

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function isOverdue(task: PersonTask): boolean {
  if (!task.dueDate) return false;
  return startOfDay(new Date(task.dueDate)) < startOfDay(new Date());
}

export type DueBucket = 'overdue' | 'today' | 'this_week' | 'this_month' | 'none' | 'custom';

/** Does a task fall in the given due-date bucket? Mirrors backend semantics. */
export function matchesDueBucket(
  task: PersonTask,
  bucket: DueBucket | '',
  custom?: { from?: string; to?: string },
): boolean {
  if (!bucket) return true;
  if (bucket === 'none') return task.dueDate === null;
  if (task.dueDate === null) return false;

  const due = startOfDay(new Date(task.dueDate));
  const today = startOfDay(new Date());

  switch (bucket) {
    case 'overdue':
      return due < today;
    case 'today':
      return due.getTime() === today.getTime();
    case 'this_week': {
      const day = (today.getDay() + 6) % 7; // 0 = Monday
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - day);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return due >= weekStart && due <= weekEnd;
    }
    case 'this_month':
      return due.getFullYear() === today.getFullYear() && due.getMonth() === today.getMonth();
    case 'custom': {
      if (custom?.from && due < startOfDay(new Date(custom.from))) return false;
      if (custom?.to && due > startOfDay(new Date(custom.to))) return false;
      return true;
    }
    default:
      return true;
  }
}

export function formatDue(due: string | null): string {
  if (!due) return '—';
  return new Date(due).toLocaleDateString('ro-RO');
}
